import { createContext, useCallback, useMemo, useRef, useState } from "react";

const NotificationContext = createContext();

export function NotificationContextProvider(props) {
  const [activeNotification, setActiveNotification] = useState("");
  const [status, setStatus] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [hasConfirm, setHasConfirm] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Store callbacks in refs so React never treats the handler itself as a
  // setState updater, and hasConfirm stays in sync with the real callback.
  const confirmCallbackRef = useRef(null);
  const closeCallbackRef = useRef(null);
  const confirmingRef = useRef(false);

  const hide = useCallback(() => {
    confirmingRef.current = false;
    setConfirming(false);
    confirmCallbackRef.current = null;
    closeCallbackRef.current = null;
    setHasConfirm(false);
    setShowNotification(false);
    setActiveNotification("");
    setStatus("");
  }, []);

  const show = useCallback(({ message, stat, onConfirm, onClose }) => {
    confirmingRef.current = false;
    setConfirming(false);
    confirmCallbackRef.current =
      typeof onConfirm === "function" ? onConfirm : null;
    closeCallbackRef.current = typeof onClose === "function" ? onClose : null;
    setHasConfirm(typeof onConfirm === "function");
    setActiveNotification(message || "");
    setStatus(stat || "");
    setShowNotification(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (confirmingRef.current) return;
    const cb = confirmCallbackRef.current;
    if (typeof cb !== "function") {
      hide();
      return;
    }

    confirmingRef.current = true;
    setConfirming(true);
    try {
      await cb();
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
      // If the callback called show() (e.g. success toast), keep that
      // notification. Only fully dismiss when the confirm callback is unchanged.
      if (confirmCallbackRef.current === cb) {
        hide();
      }
    }
  }, [hide]);

  const handleClose = useCallback(() => {
    if (confirmingRef.current) return;
    const cb = closeCallbackRef.current;
    if (typeof cb === "function") {
      cb();
    }
    hide();
  }, [hide]);

  const ctx = useMemo(
    () => ({
      message: activeNotification,
      status,
      show,
      hide,
      onConfirm: handleConfirm,
      onClose: handleClose,
      showNotification,
      hasConfirm,
      confirming,
    }),
    [
      activeNotification,
      status,
      show,
      hide,
      handleConfirm,
      handleClose,
      showNotification,
      hasConfirm,
      confirming,
    ],
  );

  return (
    <NotificationContext.Provider value={ctx}>
      {props.children}
    </NotificationContext.Provider>
  );
}

export default NotificationContext;
